import React, { useEffect, useMemo, useState } from "react";
import api, { formatEUR, formatApiErrorDetail } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, Car, Sparkles, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import WhatsAppTicketDialog from "@/components/WhatsAppTicketDialog";

export default function NewSale() {
  const [cars, setCars] = useState([]);
  const [extras, setExtras] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [selectedExtras, setSelectedExtras] = useState({});
  const [customer, setCustomer] = useState("");
  const [notes, setNotes] = useState("");
  const [payment, setPayment] = useState("efectivo");
  const [submitting, setSubmitting] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, e] = await Promise.all([api.get("/car-types"), api.get("/extras")]);
      setCars(c.data);
      setExtras(e.data);
    })();
  }, []);

  const total = useMemo(() => {
    const carPrice = selectedCar ? Number(selectedCar.price) : 0;
    const extrasPrice = extras
      .filter((e) => selectedExtras[e.id])
      .reduce((acc, e) => acc + Number(e.price), 0);
    return carPrice + extrasPrice;
  }, [selectedCar, selectedExtras, extras]);

  const selectedExtrasList = useMemo(
    () => extras.filter((e) => selectedExtras[e.id]),
    [extras, selectedExtras]
  );

  const toggleExtra = (id) =>
    setSelectedExtras((s) => ({ ...s, [id]: !s[id] }));

  const onSubmit = async () => {
    if (!selectedCar) {
      toast.error("Selecciona un tipo de auto");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        car_type_id: selectedCar.id,
        extras_ids: Object.keys(selectedExtras).filter((k) => selectedExtras[k]),
        customer_name: customer,
        notes,
        payment_method: payment,
      };
      const { data } = await api.post("/sales", payload);
      toast.success(`Venta registrada · ${formatEUR(total)}`);
      setLastSale(data);
      setTicketOpen(true);
      setSelectedCar(null);
      setSelectedExtras({});
      setCustomer("");
      setNotes("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="new-sale-page">
      <header>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
          Nueva venta
        </h1>
        <p className="text-slate-500 mt-1">
          Selecciona el tipo de auto y los extras para registrar el servicio.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Selectors */}
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="font-display font-semibold text-lg flex items-center gap-2 mb-4">
              <Car className="w-4 h-4 text-blue-600" /> Tipo de auto
            </h2>
            {cars.length === 0 ? (
              <p className="text-slate-500 text-sm">
                No hay tipos de auto. Crea uno en “Tipos de auto”.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                {cars.map((c) => {
                  const active = selectedCar?.id === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCar(c)}
                      data-testid={`car-option-${c.id}`}
                      className={`text-left p-4 rounded-lg border transition-all ${
                        active
                          ? "border-blue-600 bg-blue-50 ring-2 ring-blue-200"
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <div className="text-sm font-semibold">{c.name}</div>
                      <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">
                        {c.description || " "}
                      </div>
                      <div className="mt-3 font-display text-xl font-bold tabular-nums">
                        {formatEUR(c.price)}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="font-display font-semibold text-lg flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-blue-600" /> Extras
            </h2>
            {extras.length === 0 ? (
              <p className="text-slate-500 text-sm">
                No hay extras configurados.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {extras.map((e) => {
                  const active = !!selectedExtras[e.id];
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => toggleExtra(e.id)}
                      data-testid={`extra-option-${e.id}`}
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm border transition-colors ${
                        active
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                      }`}
                    >
                      {active && <Check className="w-3.5 h-3.5" />}
                      {e.name}
                      <span
                        className={`tabular-nums ${
                          active ? "text-white/90" : "text-slate-500"
                        }`}
                      >
                        +{formatEUR(e.price)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">Cliente (opcional)</Label>
              <Input
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="Nombre / matrícula"
                data-testid="sale-customer-input"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Método de pago</Label>
              <Select value={payment} onValueChange={setPayment}>
                <SelectTrigger data-testid="sale-payment-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  <SelectItem value="bizum">Bizum / transferencia</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="mb-1.5 block">Notas</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observaciones..."
                rows={2}
                data-testid="sale-notes-input"
              />
            </div>
          </section>
        </div>

        {/* Cart */}
        <aside className="lg:sticky lg:top-6 self-start">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-display font-semibold text-lg flex items-center gap-2 mb-4">
              <ShoppingCart className="w-4 h-4 text-blue-600" /> Resumen
            </h3>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Auto</span>
                <span className="font-medium tabular-nums">
                  {selectedCar ? formatEUR(selectedCar.price) : "—"}
                </span>
              </div>
              {selectedExtrasList.map((e) => (
                <div
                  key={e.id}
                  className="flex justify-between text-slate-600"
                >
                  <span>+ {e.name}</span>
                  <span className="tabular-nums">{formatEUR(e.price)}</span>
                </div>
              ))}
              <div className="border-t border-slate-200 pt-3 mt-3 flex items-center justify-between">
                <span className="text-slate-600">Total</span>
                <span
                  className="font-display text-3xl font-black tabular-nums text-slate-950"
                  data-testid="sale-total"
                >
                  {formatEUR(total)}
                </span>
              </div>
            </div>
            <Button
              onClick={onSubmit}
              disabled={submitting || !selectedCar}
              className="w-full mt-5 h-12 text-base bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="sale-submit-button"
            >
              {submitting ? "Guardando…" : "Completar venta"}
            </Button>
          </div>
        </aside>
      </div>

      <WhatsAppTicketDialog
        open={ticketOpen}
        onClose={() => setTicketOpen(false)}
        sale={lastSale}
      />
    </div>
  );
}
